import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getOrganizationByCommerce, type CoreOrganization } from '@/lib/zifekCore';
import {
  resolveCustomerContext,
  CUSTOMER_RELATION_CHAIN,
  type CustomerGlobalContext,
} from '@/lib/coreCustomerContext';
import {
  listEntityTimeline,
  timelineEventMeta,
  type CoreTimelineEvent,
} from '@/lib/coreTimeline';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 75. ENCART « CONTEXTE CLIENT »
// Affiche directement dans la fiche client du dashboard la RELATION GLOBALE
// du client (User → Customer profile → Business relationship) et toutes ses
// relations réelles (RDV, réservations, commandes, avis, favoris, documents,
// conversations…) reconstruites depuis le Core, SANS dupliquer les données.
// ─────────────────────────────────────────────────────────────

interface Props {
  /** Id du marchand (users.id) — résout l'organisation Core. */
  commerceId: number | null | undefined;
  /** Id du client tel que manipulé par la fiche (users.id ou clientshop.idclient). */
  customerId: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function CustomerContextPanel({ commerceId, customerId, clientName, clientEmail, clientPhone }: Props) {
  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [context, setContext] = useState<CustomerGlobalContext | null>(null);
  const [timeline, setTimeline] = useState<CoreTimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!commerceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const o = await getOrganizationByCommerce(commerceId);
      setOrg(o);
      if (!o) {
        setContext(null);
        setTimeline([]);
        return;
      }

      // Résolution de la fiche client du Core (par user_id puis par email).
      let coreId: number | null = null;
      const numeric = parseInt(customerId, 10);
      if (!Number.isNaN(numeric)) {
        const { data } = await supabase
          .from('core_customers')
          .select('id')
          .eq('organization_id', o.id)
          .eq('user_id', numeric)
          .maybeSingle();
        coreId = (data?.id as number) ?? null;
      }
      if (!coreId && clientEmail) {
        const { data } = await supabase
          .from('core_customers')
          .select('id')
          .eq('organization_id', o.id)
          .eq('email', clientEmail)
          .maybeSingle();
        coreId = (data?.id as number) ?? null;
      }

      if (coreId) {
        const globalContext = await resolveCustomerContext({
          organizationId: o.id,
          commerceId,
          customerId: coreId,
        });
        setContext(globalContext);
        try {
          setTimeline(await listEntityTimeline(o.id, 'customer', coreId));
        } catch {
          setTimeline([]);
        }
      } else {
        setContext(null);
        setTimeline([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId, customerId, clientEmail]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={load} className="ml-auto underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {/* Chaîne de relation globale */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className="ri-links-line"></i>
            </span>
            <div>
              <h4 className="text-sm font-semibold text-foreground-900">Relation globale</h4>
              <p className="text-[11px] text-foreground-400">User → Customer profile → Business relationship</p>
            </div>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i> Actualiser
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {CUSTOMER_RELATION_CHAIN.map((node, i, arr) => {
            const resolved = node.key === 'user' ? Boolean(commerceId) : node.key === 'customer' ? Boolean(context?.customer) : Boolean(org);
            return (
              <span key={node.key} className="flex items-center gap-2">
                <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${resolved ? 'bg-secondary-100 text-secondary-900' : 'bg-background-100 text-foreground-500'}`}>
                  <i className={node.icon}></i>
                  {node.label}
                  <i className={resolved ? 'ri-checkbox-circle-fill text-secondary-600' : 'ri-question-line text-foreground-400'}></i>
                </span>
                {i < arr.length - 1 && <i className="ri-arrow-right-line text-foreground-300"></i>}
              </span>
            );
          })}
        </div>
      </div>

      {!org ? (
        <div className="rounded-lg border border-dashed border-background-300/60 bg-background-50 px-4 py-8 text-center">
          <i className="ri-flashlight-line text-2xl text-foreground-300"></i>
          <p className="text-sm text-foreground-500 mt-2">Socle central non initialisé pour ce commerce.</p>
          <p className="text-xs text-foreground-400 mt-1">
            Le contexte global client s’active dès que l’organisation Core existe.
          </p>
        </div>
      ) : !context ? (
        <div className="rounded-lg border border-dashed border-background-300/60 bg-background-50 px-4 py-8 text-center">
          <i className="ri-user-search-line text-2xl text-foreground-300"></i>
          <p className="text-sm text-foreground-500 mt-2">
            {clientName || 'Ce client'} n’est pas encore relié à un profil client du Core.
          </p>
          <p className="text-xs text-foreground-400 mt-1">
            Créez la fiche client dans le socle (ou renseignez son email) pour agréger automatiquement ses relations.
          </p>
        </div>
      ) : (
        <>
          {/* Profil client Core */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <span className="text-base font-bold text-accent-700">
                  {(context.customer?.displayName || clientName || '?').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900 truncate">
                  {context.customer?.displayName || clientName || `Client #${customerId}`}
                </p>
                <p className="text-[11px] text-foreground-500 truncate">
                  {context.customer?.email || clientEmail || '—'}
                  {context.customer?.phone ? ` · ${context.customer.phone}` : clientPhone ? ` · ${clientPhone}` : ''}
                </p>
              </div>
              <div className="ml-auto flex gap-2 text-center">
                <div className="bg-background-100 rounded-lg px-3 py-1.5">
                  <p className="text-base font-bold text-foreground-900">{context.totals.relations}</p>
                  <p className="text-[10px] text-foreground-500">relations</p>
                </div>
                <div className="bg-background-100 rounded-lg px-3 py-1.5">
                  <p className="text-base font-bold text-foreground-900">{context.totals.categories}</p>
                  <p className="text-[10px] text-foreground-500">catégories</p>
                </div>
              </div>
            </div>
            {context.customer?.companyName && (
              <p className="text-xs text-foreground-600 mt-2 flex items-center gap-1.5">
                <i className="ri-building-2-line text-foreground-400"></i>
                {context.customer.companyName}
              </p>
            )}
          </div>

          {/* Relations groupées */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {context.groups.map((group) => (
              <div key={group.key} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-background-200/60">
                  <span className="w-7 h-7 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className={`${group.icon} text-sm`}></i>
                  </span>
                  <h5 className="text-xs font-semibold text-foreground-800">{group.label}</h5>
                  <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                    {group.items.length}
                  </span>
                </div>
                {group.items.length === 0 ? (
                  <p className="px-3.5 py-3 text-[11px] text-foreground-400">Aucune relation.</p>
                ) : (
                  <div className="divide-y divide-background-200/50">
                    {group.items.slice(0, 4).map((item) => (
                      <div key={`${group.key}-${item.id}`} className="flex items-center gap-2 px-3.5 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-foreground-800 truncate">{item.title}</p>
                          {item.subtitle && <p className="text-[11px] text-foreground-400 truncate">{item.subtitle}</p>}
                        </div>
                        {item.amount != null && (
                          <span className="text-xs font-semibold text-primary-600 whitespace-nowrap">
                            {Number(item.amount).toLocaleString()} {item.currency || ''}
                          </span>
                        )}
                        {item.date && (
                          <span className="text-[10px] text-foreground-400 whitespace-nowrap hidden sm:block">{formatDate(item.date)}</span>
                        )}
                      </div>
                    ))}
                    {group.items.length > 4 && (
                      <p className="px-3.5 py-1.5 text-[10px] text-foreground-400">
                        +{group.items.length - 4} autre{group.items.length - 4 > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Timeline du client */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/60">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-history-line"></i>
              </span>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900">Timeline du client</h4>
                <p className="text-[11px] text-foreground-400">Toutes les interactions liées à ce client</p>
              </div>
            </div>
            {timeline.length === 0 ? (
              <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune activité pour le moment.</p>
            ) : (
              <div className="px-4 py-3">
                <ol className="relative border-l border-background-200/70 ml-2">
                  {timeline.slice(0, 12).map((ev) => {
                    const meta = timelineEventMeta(ev.event_type);
                    return (
                      <li key={ev.id} className="ml-5 pb-4 last:pb-0">
                        <span className={`absolute -left-[13px] w-6 h-6 rounded-full flex items-center justify-center ${meta.className}`}>
                          <i className={`${ev.icon || meta.icon} text-[11px]`}></i>
                        </span>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="text-sm font-semibold text-foreground-900">{ev.title}</span>
                          {ev.amount != null && (
                            <span className="text-xs font-semibold text-primary-600">
                              {Number(ev.amount).toLocaleString()} {ev.currency || ''}
                            </span>
                          )}
                        </div>
                        {ev.description && <p className="text-xs text-foreground-600 mt-0.5">{ev.description}</p>}
                        <p className="text-[11px] text-foreground-400 mt-0.5">
                          {formatDate(ev.occurred_at)}
                          {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}